import React from 'react'
import { createPortal } from 'react-dom'
import { api } from '../api/client'
import type { FormSubmission, HealthAnswer } from '../api/client'
import type { Preference } from '../types/app'
import FormView from '../forms/FormView'
import { healthFlagInputs } from '../forms/status'
import { useDialog } from '../hooks/useDialog'
import { saveErrorMessage } from './settings/toast'
import '../components/Sheet.css'
import './ClientForms.css'

interface Props {
  submissionId: string
  clientId: string
  onClose: () => void
  // Called with the client's preferences after a health flag is added.
  onPreferencesChange?: (preferences: Preference[]) => void
}

const noop = () => {}

function signedAtLabel(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

// FormSubmissionSheet shows a signed form as it was signed: the health
// answers first, each offered as a health flag, then the whole form in order,
// then the signature block. Print gives a clean copy for her files or an insurer.
export default function FormSubmissionSheet({ submissionId, clientId, onClose, onPreferencesChange }: Props) {
  const [submission, setSubmission] = React.useState<FormSubmission | null>(null)
  const [loadFailed, setLoadFailed] = React.useState(false)
  const [preferences, setPreferences] = React.useState<Preference[] | null>(null)
  const [adding, setAdding] = React.useState<string | null>(null)
  const [error, setError] = React.useState('')

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose)

  const load = React.useCallback(() => {
    setLoadFailed(false)
    api.getFormSubmission(submissionId).then(setSubmission).catch(() => setLoadFailed(true))
  }, [submissionId])
  React.useEffect(() => {
    load()
  }, [load])
  React.useEffect(() => {
    api.getPreferences(clientId).then(setPreferences).catch(() => setPreferences(null))
  }, [clientId])

  const isFlagged = (h: HealthAnswer) =>
    !!preferences && preferences.some((p) => p.is_health_flag && p.label === h.label && p.value === h.value)

  const addFlag = async (h: HealthAnswer) => {
    if (!preferences || adding) return
    const next = healthFlagInputs(preferences, h)
    if (!next) return
    setAdding(h.field_id)
    setError('')
    try {
      const saved = await api.putPreferences(clientId, next)
      setPreferences(saved)
      onPreferencesChange?.(saved)
    } catch (e) {
      setError(saveErrorMessage(e))
    } finally {
      setAdding(null)
    }
  }

  return createPortal(
    <div className="sheet-overlay forms-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="sheet-content"
        role="dialog"
        aria-modal="true"
        aria-label={submission ? `${submission.template_name}, signed` : 'Signed form'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle no-print"></div>
        {!submission ? (
          <div className="sheet-body">
            {loadFailed ? (
              <>
                <p role="alert">The form could not be loaded.</p>
                <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
                  Try again
                </button>
              </>
            ) : (
              <p aria-busy="true">Loading…</p>
            )}
            <button type="button" className="btn btn-quiet btn-block no-print" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="sheet-header">
              <small>Signed {signedAtLabel(submission.signed_at)}</small>
              <h2>{submission.template_name}</h2>
            </div>
            <div className="sheet-body">
              {submission.health.length > 0 && (
                <section className="submission-block" aria-label="Health answers">
                  <h3>Health answers</h3>
                  {error && (
                    <p className="field-error" role="alert">
                      {error}
                    </p>
                  )}
                  <ul className="submission-health">
                    {submission.health.map((h) => (
                      <li key={h.field_id} className="submission-health__item">
                        <span>
                          <strong>{h.label}</strong>
                          <br />
                          {h.value}
                        </span>
                        {isFlagged(h) ? (
                          <span className="field-hint no-print">Added to health flags</span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-secondary btn-compact no-print"
                            disabled={!preferences || adding !== null}
                            aria-label={`Add to health flags: ${h.label}`}
                            onClick={() => addFlag(h)}
                          >
                            Add to health flags
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="submission-block" aria-label="Answers">
                <FormView fields={submission.fields} answers={submission.answers} onChange={noop} readOnly />
              </section>

              <section className="submission-block submission-signature" aria-label="Signature">
                <h3>Signature</h3>
                <p>
                  Name: <strong>{submission.signed_name}</strong>
                </p>
                {submission.has_signature ? (
                  <img src={api.formSignatureUrl(submission.id)} alt={`Signature of ${submission.signed_name}`} />
                ) : (
                  <p>Signed by typing their name</p>
                )}
                {submission.guardian_name && (
                  <p>
                    Parent or guardian: {submission.guardian_name}
                    {submission.guardian_relationship ? ` (${submission.guardian_relationship})` : ''}
                  </p>
                )}
                <p className="field-hint">
                  {submission.sent_to === 'in_person'
                    ? 'Filled in at the studio.'
                    : submission.sent_to
                      ? `Link sent to ${submission.sent_to}.`
                      : ''}{' '}
                  Version {submission.version}.
                </p>
              </section>
            </div>
            <div className="sheet-stack no-print">
              <button type="button" className="btn btn-secondary btn-block" onClick={() => window.print()}>
                Print
              </button>
              <button type="button" className="btn btn-quiet btn-block" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
